import {
  useState, useCallback, useReducer, useEffect, useMemo,
} from 'react';
import {
  Modal,
  Stepper,
  ModalStepperContent,
  ModalStepperMenu,
} from '@visionplanner/ui-react-material';
import * as requests from './partner-onboarding.requests';
import { PartnerOnboradingWrapper } from './partner-styles';
import { PartnerContext } from './partner-context';
import FirmKeyDetails from './steps/firm-key-details';
import FirmAddress from './steps/firm-address';
import { types, dataFetchReducer, initialState } from './partner-onboarding.reducer';
import AddEmployees from './steps/add-employees';
import OtherSoftwaresUsed from './steps/other-softwares-used';
import FirmLogo from './steps/firm-logo';
import InvoiceInformation from './steps/invoice-information';
import Review from './steps/review';
import OnBoardGreeting from './on-board-greeting';
import { useDataSource } from '../../common/custom-hooks/use-data-source';
import { useAsyncCallback } from '../../common/custom-hooks/use-async-callback';

/** Component to update partner-details which will,
 *  filled by partner himself.
 */

const PARTNER_ONBOARDING = Object.freeze({
  startOnboardingStep: 3,
  endOnBoardingStep: 11,
});

const PartnerOnboarding = () => {
  const [activeStep, setActiveStep] = useState(null);
  const [firmLogo, setFirmLogo] = useState({});

  const [state, dispatch] = useReducer(dataFetchReducer, initialState);

  const [fieldOptions] = useDataSource(requests.getFieldOptions);
  const [steps] = useDataSource(requests.getPartnerSteps);
  const [validations] = useDataSource(requests.getValidationJson);

  const isOnBoardingStarted = useMemo(() => (activeStep ? activeStep <= PARTNER_ONBOARDING.startOnboardingStep : false), [activeStep]);
  const isOnBoardingEnded = useMemo(() => (activeStep ? activeStep >= PARTNER_ONBOARDING.endOnBoardingStep : false), [activeStep]);

  const getStepContent = useCallback((stepIndex) => {
    const {
      sbiOptions, countryTypes,
      administrationSoftwares, payrollSoftwares, taxationSoftwares,
    } = fieldOptions;
    switch (stepIndex) {
      case 5:
        return <FirmAddress validations={validations} countryTypes={countryTypes} />;
      case 6:
        return <AddEmployees />;
      case 7:
        return <OtherSoftwaresUsed administrationSoftwares={administrationSoftwares} payrollSoftwares={payrollSoftwares} taxationSoftwares={taxationSoftwares} />;
      case 8:
        return <FirmLogo firmLogo={firmLogo} setFirmLogo={setFirmLogo} />;
      case 9:
        return <InvoiceInformation />;
      case 10:
        return <Review fieldOptions={fieldOptions} />;
      default:
        return <FirmKeyDetails validations={validations} sbiOptions={sbiOptions} />;
    }
  }, [fieldOptions, firmLogo, validations]);

  const fetchPartnerDetails = useAsyncCallback(async () => {
    const partnerDetails = await requests.getPartnerDetails();
    const firmLogoDetails = await requests.getFirmLogo(partnerDetails.partnerId);
    dispatch({ type: types.fetchPartnerDetails, payload: partnerDetails });
    setActiveStep(partnerDetails.currentStep === 4 ? partnerDetails.currentStep - 1 : partnerDetails.currentStep);
    setFirmLogo(firmLogoDetails);
  }, []);

  useEffect(() => {
    fetchPartnerDetails();
  }, [fetchPartnerDetails]);

  return (
    fieldOptions && steps && activeStep
    && (
      <PartnerOnboradingWrapper>
        <Modal
          onClose={() => true}
          title="Activeer Visionplanner voor jouw kantoor"
          preventDismissalOnOutsideClick
          dataTa="partner-onboarding"
          open
          showCloseIcon={false}
        >
          {isOnBoardingStarted && (
            <OnBoardGreeting
              dataTa="begin-onboarding"
              onClick={() => setActiveStep(4)}
              buttonLabel="Begin"
              imageUrl="/assets/images/start-onboarding.png"
              title="Basisinstellingen Visionplanner !"
              description="Deze wizard helpt je met de basisinstellingen van Visionplanner."
            />
          )}
          {isOnBoardingEnded && (
            <OnBoardGreeting
              dataTa="exit-wizard"
              onClick={() => {
                window.location.assign(visionplannerUrl);
              }}
              buttonLabel="Sluiten"
              imageUrl="/assets/images/end-onboarding.png"
              title="Gefeliciteerd !"
              description="Je hebt deze wizard succesvol afgerond.
                  Je kunt Visionplanner nu gebruiken."
            />
          )}
          {!isOnBoardingStarted && !isOnBoardingEnded && (
            <>
              <div className="flex full-height overflow-hidden">
                <ModalStepperMenu className="overflow-auto">
                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    steps={steps.partnerSteps}
                    activateStep={setActiveStep}
                  />
                </ModalStepperMenu>
                <ModalStepperContent>
                  <PartnerContext.Provider value={{
                    setActiveStep,
                    activeStep,
                    state,
                    dispatch,
                  }}
                  >
                    {getStepContent(activeStep)}
                  </PartnerContext.Provider>
                </ModalStepperContent>
              </div>
            </>
          )}
        </Modal>

      </PartnerOnboradingWrapper>
    )
  );
};

export default PartnerOnboarding;
